import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import type { ColumnDef } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable, SortableHeader } from '@/components/ui/data-table';
import {
  ListPage,
  ListPageHeader,
  ListPageTitle,
  ListPageActions,
  ListPageCard,
  ListPageFilters,
  ListPageContent,
} from '@/components/ui/list-page';
import { useApp } from '@/components/AppLayout';
import { ClientDialog } from '@/components/ClientDialog';
import { ClientImportDialog } from '@/components/ClientImportDialog';
import { clientApi, queryFn, type Client } from '@/lib/api';
import { MoreVertical, Users, UserPlus, Upload } from 'lucide-react';

function ClientsContent() {
  const { nav } = useBusinessNav();
  const { currentBusiness, isViewOnly } = useApp();
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const { data: clients = [], isLoading } = useQuery({
    queryKey: ['clients', currentBusiness?.id, searchQuery],
    queryFn: () => queryFn(clientApi.list(currentBusiness!.id, searchQuery)),
    enabled: !!currentBusiness,
  });

  const invalidateClients = () => queryClient.invalidateQueries({ queryKey: ['clients'] });

  const openAddDialog = () => {
    setEditingClient(null);
    setDialogOpen(true);
  };

  // Open add dialog when navigated with ?new=true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('new') === 'true') {
      openAddDialog();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const openEditDialog = (client: Client) => {
    setEditingClient(client);
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingClient(null);
  };

  const handleDeleteClient = async (id: string) => {
    if (!confirm('Are you sure you want to delete this client?')) return;
    const response = await clientApi.delete(id);
    if (response.error) {
      alert(response.error);
    } else {
      invalidateClients();
    }
  };

  const handleExport = () => {
    const csv = [
      ['Name', 'Company', 'Email', 'Phone', 'Balance'].join(','),
      ...clients.map(c => [
        c.billing_name,
        c.company_name,
        c.primary_email,
        c.primary_phone?.number || '',
        ''
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients.csv';
    a.click();
  };

  const columns: ColumnDef<Client>[] = [
    {
      accessorKey: 'billing_name',
      header: ({ column }) => <SortableHeader column={column} title="Name" />,
      cell: ({ row }) => (
        <div className="min-w-0">
          <div className="text-sm font-medium truncate">{row.original.billing_name}</div>
          {row.original.company_name && (
            <div className="text-xs text-muted-foreground truncate">{row.original.company_name}</div>
          )}
        </div>
      ),
    },
    {
      accessorKey: 'primary_email',
      header: ({ column }) => <SortableHeader column={column} title="Email" />,
      cell: ({ row }) => (
        <span className="text-sm">{row.getValue('primary_email')}</span>
      ),
    },
    {
      accessorKey: 'primary_phone',
      header: 'Phone',
      cell: ({ row }) => {
        const phone = row.original.primary_phone;
        return (
          <span className="text-sm">
            {phone?.country_code && phone?.number
              ? `${phone.country_code} ${phone.number}`
              : ''}
          </span>
        );
      },
    },
    {
      id: 'balance',
      header: 'Balance',
      cell: () => <span className="text-sm"></span>,
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="text-right">
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                openEditDialog(row.original);
              }}>
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => {
                e.stopPropagation();
                nav(`/invoices/create?clientId=${row.original.id}`);
              }}>
                Create Invoice
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteClient(row.original.id);
                }}
                className="text-destructive"
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  return (
    <>
      <ListPage>
          <ListPageHeader>
            <ListPageTitle>Clients</ListPageTitle>
            <ListPageActions>
              <Button variant="outline" onClick={handleExport} className="rounded-full">
                Export
              </Button>
              {!isViewOnly && (
                <Button variant="outline" onClick={() => setImportDialogOpen(true)} className="rounded-full">
                  <Upload className="h-4 w-4" />
                  Upload Clients
                </Button>
              )}
              {!isViewOnly && (
                <Button onClick={openAddDialog} className="rounded-full">
                  <UserPlus className="h-4 w-4" />
                  Add Client
                </Button>
              )}
            </ListPageActions>
          </ListPageHeader>

          <ListPageCard>
            <ListPageFilters>
              <Input
                placeholder="Search clients..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="max-w-sm bg-white"
              />
            </ListPageFilters>

            <ListPageContent>
              <DataTable
                columns={columns}
                data={clients}
                loading={isLoading}
                onRowClick={(client) => nav(`/clients/${client.id}`)}
                noBorder
                emptyIcon={<Users className="h-6 w-6 text-muted-foreground" />}
                emptyTitle={searchQuery ? "No clients found" : "No clients yet"}
                emptyDescription={searchQuery ? "Try adjusting your search." : "Add your first client to get started."}
              />
            </ListPageContent>
          </ListPageCard>
        </ListPage>

      {currentBusiness && (
        <ClientDialog
          open={dialogOpen}
          onClose={closeDialog}
          businessId={currentBusiness.id}
          client={editingClient}
          onSaved={invalidateClients}
        />
      )}

      {currentBusiness && (
        <ClientImportDialog
          open={importDialogOpen}
          onClose={() => setImportDialogOpen(false)}
          businessId={currentBusiness.id}
          onImported={invalidateClients}
        />
      )}
    </>
  );
}

export function Clients() {
  return <ClientsContent />;
}
